<template>
  <div class="container">
    <div class="title">
      <router-link to="/userCenter">
        <img src="@/assets/images/arrow_left.svg" class="back_btn" />
      </router-link>
      Account management
    </div>
    <div class="user_section">
      <span class="label">Change email address</span>
      <span class="action_btn" @click="handleInitEditModal('email')">
        <span class="email">{{ store.state.currentAccount.email }}</span>
        <img src="@/assets/images/arrow_right.svg" />
      </span>
    </div>
    <div class="user_section" @click="handleInitEditModal('password')">
      <span class="label">Change password</span>
      <span class="action_btn">
        <img src="@/assets/images/arrow_right.svg" />
      </span>
    </div>
    <a-modal class="user_center_modal" v-model:open="emailVisible" @cancel="handleCancel" :footer="null">
      <template #title>{{ editType == 'email' ? 'Change email address' : 'Change password' }}</template>
      <template v-if="editType == 'email'">
        <div class="tip">
          You can change your email address after completing the verification of your new email address
        </div>
        <a-form ref="formEmailRef" :model="formState" class="modal_form">
          <EmailInputItem :initial-email="formState.email" @change-email="handleChangeEmail" />
          <a-form-item validateTrigger="blur" label="" name="code" :rules="[{ required: true, message: 'Enter code' }]">
            <a-input v-model:value="formState.code" placeholder="Enter code" />
            <button
              class="send_code_btn"
              @click.prevent="sendSMSCode"
              :disabled="isSMSCodeDisabled || isSending"
              :style="{ color: sendSMSCodeText !== 'Send code' || isSending ? 'rgba(61, 61, 61, 0.5)' : '' }"
            >
              <LoadingOutlined v-if="isSending" spin />
              {{ isSending ? 'Sending' : sendSMSCodeText }}
            </button>
          </a-form-item>
          <a-button
            type="primary"
            html-type="button"
            class="submit_btn"
            @click.prevent="handleEmailSubmit"
            :disabled="isSubmitDisabled"
          >
            Done
          </a-button>
        </a-form>
      </template>
      <template v-else>
        <div class="tip">
          Change password via registered email address
        </div>
        <a-form ref="formPwdRef" :model="formState" class="modal_form">
          <a-form-item validateTrigger="change" label="" name="newPassword" :rules="[{ validator: validatePass1 }]">
            <a-input-password
              v-model:value="formState.newPassword"
              placeholder="Enter new password"
              autocomplete="off"
              :visibilityToggle="true"
            />
            <div v-if="isShowValidPwd" class="valid_pwd">
              <img
                v-if="isValidPwdCharacter"
                class="pwd_icon"
                src="@/assets/images/pwd_success.svg"
                alt="Validation Icon"
              />
              <img
                v-if="!isValidPwdCharacter"
                class="pwd_icon"
                src="@/assets/images/pwd_error.svg"
                alt="Validation Icon"
              />
              <span :class="{ valid_success: isValidPwdCharacter }">The password must contain letters and numbers</span>
            </div>
            <div v-if="isShowValidPwd" class="valid_pwd">
              <img
                v-if="isValidPwdLength"
                class="pwd_icon"
                src="@/assets/images/pwd_success.svg"
                alt="Validation Icon"
              />
              <img
                v-if="!isValidPwdLength"
                class="pwd_icon"
                src="@/assets/images/pwd_error.svg"
                alt="Validation Icon"
              />
              <span :class="{ valid_success: isValidPwdLength }">The password must be at least 8 characters long</span>
            </div>
          </a-form-item>
          <a-form-item validateTrigger="blur" label="" name="confirmPassword" :rules="[{ validator: validatePass2 }]">
            <a-input-password
              v-model:value="formState.confirmPassword"
              placeholder="Enter again"
              autocomplete="off"
              :visibilityToggle="true"
            />
          </a-form-item>
          <a-button type="primary" class="submit_btn" @click.prevent="handlePwdSubmit" :disabled="isSubmitDisabled">
            Done
          </a-button>
        </a-form>
      </template>
    </a-modal>
  </div>
</template>

<script setup>
import { message } from 'ant-design-vue';
import { LoadingOutlined } from '@ant-design/icons-vue';
import { onMounted, onUnmounted, ref, reactive } from 'vue';
import { useStore } from 'vuex';

import { updateEmail, sendVerifyCode, updatePwd } from '@/api/userCenter.js';
import EmailInputItem from '@/components/EmailInputItem.vue';

const store = useStore();

const handleCancel = () => {
  emailVisible.value = false;
};

const emailVisible = ref(false);
const formState = reactive({
  email: '',
  code: '',
  newPassword: '',
  confirmPassword: '',
});
const editType = ref('email');

const isShowValidPwd = ref(false);
const isValidPwdCharacter = ref(false);
const isValidPwdLength = ref(false);
const validatePass1 = async (_rule, value) => {
  isShowValidPwd.value = true;
  if (!/(?=.*[a-zA-Z])(?=.*[0-9])/.test(value)) {
    isValidPwdCharacter.value = false;
  } else {
    isValidPwdCharacter.value = true;
  }
  if (value.length < 8) {
    isValidPwdLength.value = false;
  } else {
    isValidPwdLength.value = true;
  }
  if (!isValidPwdCharacter.value || !isValidPwdLength.value) {
    return Promise.reject();
  } else {
    return Promise.resolve();
  }
};
const validatePass2 = async (_rule, value) => {
  if (value === '') {
    return Promise.reject('Enter a new password');
  } else if (value !== formState.newPassword) {
    return Promise.reject('The two passwords do not match');
  } else {
    return Promise.resolve();
  }
};

const handleInitEditModal = (type) => {
  emailVisible.value = true;
  // 本次已发送过验证码且连续打开邮箱弹窗时，保留倒计时与邮箱；否则重置
  const keepState = type === 'email' && editType.value === 'email' && !!intervalId.value;
  editType.value = type;
  formState.code = '';
  formState.newPassword = '';
  formState.confirmPassword = '';
  if (!keepState) {
    formState.email = '';
    if (!intervalId.value) {
      isSMSCodeDisabled.value = false;
      sendSMSCodeText.value = 'Send code';
    }
  }
};

const handleChangeEmail = (email) => {
  formState.email = email;
};

const formEmailRef = ref(null);
const isSubmitDisabled = ref(false);
const isSMSCodeDisabled = ref(false);
const isSending = ref(false);
const intervalId = ref(null);
const sendSMSCodeText = ref('Send code');

const startCountdown = (time) => {
  if (!time) return;
  if (intervalId.value) clearInterval(intervalId.value);
  isSMSCodeDisabled.value = true;
  var timeLeft = time;
  intervalId.value = setInterval(() => {
    if (timeLeft > 0) {
      timeLeft--;
      sendSMSCodeText.value = timeLeft + ' resend';
    } else {
      clearInterval(intervalId.value);
      timeLeft = 60;
      isSMSCodeDisabled.value = false;
      sendSMSCodeText.value = 'Send code';
    }
  }, 1000);
};

const sendSMSCode = () => {
  formEmailRef.value.validateFields(['email']).then(() => {
    isSending.value = true;
    sendVerifyCode({ email: formState.email }).then((res) => {
      isSending.value = false;
      startCountdown(60);
      if (res.code == 0) {
        message.success(res.msg);
      } else {
        message.error(res.msg);
      }
    }).catch(() => {
      isSending.value = false;
    });
  });
};

const handleEmailSubmit = () => {
  formEmailRef.value.validate().then(() => {
    isSubmitDisabled.value = true;
    updateEmail({
      email: formState.email,
      code: formState.code,
    }).then((res) => {
      isSubmitDisabled.value = false;
      if (res.code == 0) {
        message.success(res.msg);
        window.location.reload();
      } else {
        message.error(res.msg);
      }
    });
  });
};

const formPwdRef = ref(null);
const handlePwdSubmit = () => {
  formPwdRef.value.validate().then(() => {
    isSubmitDisabled.value = true;
    updatePwd({
      newPassword: formState.newPassword,
      confirmPassword: formState.confirmPassword,
    }).then((res) => {
      isSubmitDisabled.value = false;
      if (res.code == 0) {
        message.success(res.msg);
        handleCancel();
      } else {
        message.error(res.msg);
      }
    });
  });
};

onMounted(() => {});

onUnmounted(() => {
  if (intervalId.value) clearInterval(intervalId.value);
});
</script>

<style lang="less">
.user_center_modal {
  color: #05644f;
  font-size: 16px;
  .ant-modal-title {
    color: #05644f;
    font-size: 20px;
    font-weight: bold;
    line-height: normal;
  }
  .ant-modal-close-icon {
    color: #05644f;
  }
  .tip {
    font-size: 12px;
    color: #888;
  }
  .modal_form {
    margin-top: 20px;
    .business_input.ant-select {
      &.ant-select-status-error .ant-select-selector {
        border-color: #ff7875 !important;
      }
      .ant-select-selector {
        border: 1px solid rgba(5, 100, 79, 0.5) !important;
        box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.03) !important;
        border-radius: 16px;
        height: 46px;
        .ant-select-selection-search-input {
          font-size: 16px;
          padding: 10px 0;
          height: auto;
          color: #05644f;
        }
        .ant-select-selection-placeholder {
          color: rgba(5, 100, 79, 0.33);
          font-size: 16px;
          line-height: 42px;
        }
      }
    }
    .ant-input,
    .ant-input-password {
      border: 1px solid rgba(5, 100, 79, 0.5);
      box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.03);
      border-radius: 16px;
      padding: 10px 24px;
      font-size: 14px;
      color: #05644f;
    }
    .ant-input-password {
      padding: 0 10px 0 0;
      .ant-input {
        border: none;
      }
    }
    .ant-input-password-icon,
    .ant-input-password-icon:hover {
      color: #05644f;
      font-size: 18px;
    }
    .ant-input::placeholder {
      color: rgba(5, 100, 79, 0.33);
    }
    .send_code_btn {
      position: absolute;
      right: 24px;
      top: 12px;
      color: #05644f;
      background: transparent;
      border: none;
      cursor: pointer;
      font-weight: 500;
      min-width: 30px;
    }
    .valid_pwd {
      margin: 10px 0;
      font-size: 12px;
      color: #f04646;
      .pwd_icon {
        width: 14px;
        height: 14px;
        margin-right: 6px;
        vertical-align: text-bottom;
      }
      .valid_success {
        color: #05644f;
      }
    }
  }
  .submit_btn {
    background: #05644f;
    border-radius: 100px;
    color: #fff;
    font-size: 18px;
    font-weight: bold;
    line-height: normal;
    display: block;
    width: 100%;
    padding: 10px 20px;
    height: auto;
    margin-top: 20px;
    &:hover {
      background: #05644f;
      color: #fff;
    }
    .btn_arrow {
      margin-left: 10px;
      font-size: 18px;
    }
  }
}
</style>

<style lang="less" scoped>
.container {
  position: relative;
  max-width: 760px;
  margin: 0 auto;
  padding: 100px 0 120px;
  .title {
    font-size: 28px;
    font-weight: 700;
    color: #05644f;
    text-align: left;
    letter-spacing: -0.3px;
    margin-bottom: 40px;
    .back_btn {
      margin-right: 12px;
      cursor: pointer;
      width: 24px;
      height: 24px;
    }
  }
  .user_section {
    margin: 0 auto 24px;
    border-radius: 20px;
    background: #ffffff;
    box-shadow: 0px 10px 10px 0px rgba(0, 0, 0, 0.05);
    padding: 32px 40px;
    width: 760px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    .label {
      color: #1a1a1a;
      font-size: 16px;
      font-weight: 500;
      letter-spacing: 0.1px;
    }
    .action_btn {
      margin-left: auto;
      cursor: pointer;
      color: rgba(5, 100, 79, 0.6);
      display: flex;
      align-items: center;
      img {
        width: 20px;
        height: 20px;
        margin-left: 8px;
      }
      .email {
        font-size: 14px;
        font-weight: 400;
        letter-spacing: 0.2px;
      }
    }
  }
  // 手机端适配（≤640px）：卡片固定 760px 在窄屏横向溢出，信息行改上下两行
  @media (max-width: 640px) {
    box-sizing: border-box;
    // 容器是 Layout column flex 的 flex item，长邮箱 nowrap 的 min-content 会沿 cross-axis 撑破视口，需封顶
    max-width: 100%;
    padding: 72px 12px 40px;
    .title {
      font-size: 20px;
      margin-bottom: 20px;
      .back_btn {
        width: 20px;
        height: 20px;
        margin-right: 8px;
      }
    }
    .user_section {
      box-sizing: border-box;
      width: auto;
      flex-wrap: wrap;
      margin-bottom: 16px;
      padding: 18px 20px;
      .label {
        width: 100%;
        font-size: 15px;
      }
      .action_btn {
        width: 100%;
        margin: 12px 0 0;
        padding-top: 12px;
        border-top: 1px solid rgba(5, 100, 79, 0.1);
        justify-content: flex-end;
        min-width: 0;
        .email {
          min-width: 0;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
      }
    }
  }
}
</style>
